import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ariaSortOf } from "@/utils/aria-sort";
import {
  ColumnDef,
  OnChangeFn,
  SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Inbox } from "lucide-react";
import { SECTOR_EMAILS_LABELS } from "../messages";
import { SectorRecipient } from "../types";

interface DataTableProps {
  columns: ColumnDef<SectorRecipient>[];
  data: SectorRecipient[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

export function DataTable({
  columns,
  data,
  sorting,
  onSortingChange,
}: DataTableProps): JSX.Element {
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:block">
      <Table>
        <TableHeader className="bg-gray-50/80">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  aria-sort={ariaSortOf(
                    header.column.getCanSort(),
                    header.column.getIsSorted(),
                    header.column.getSortIndex()
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="!h-auto !py-16 text-center"
              >
                <div className="flex flex-col items-center gap-3 text-muted-foreground">
                  <Inbox className="h-8 w-8" />
                  {SECTOR_EMAILS_LABELS.EMPTY_STATE}
                </div>
              </TableCell>
            </TableRow>
          )}

          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={row.original.active ? "" : "opacity-50"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
